/** Hand-mirrored TS types for backend/oia_simulator/oia_sim/agent/contract.py and the thread/draft/document routes. */
import type * as RT from "../order-sim/runThrough"
import type { SourcesPayload } from "../order-sim/sources"

export const STAGES = ["intake", "grounds", "preliminary", "findings", "relief", "drafting"] as const
export type Stage = (typeof STAGES)[number]
/** A checkpoint may also pause to save progress (`continue`); a landed stage never is. */
export type CheckpointStage = Stage | "continue"

export type SimMode = "guided" | "just_draft"
export type PendingKind = "question" | "checkpoint" | "document" | "stop"
export type Author = "agent" | "user"

export const DOC_STATUSES = ["queued", "rendering", "indexing", "summarising", "ready", "failed"] as const
export type DocStatus = (typeof DOC_STATUSES)[number]

// ---- Event payloads (one per event type, in contract.py order) ----

/** `message` is the user's text for this run; `answered_stage` is set when the run answers a stage checkpoint. */
export interface RunStartedPayload { mode: SimMode; message: string; answered_stage?: CheckpointStage }
export const STEP_STATUSES = ["running", "done", "error"] as const
export type StepStatus = (typeof STEP_STATUSES)[number]
export const STEP_KINDS = ["read", "search", "record", "draft", "check", "ask", "think", "other"] as const
export type StepKind = (typeof STEP_KINDS)[number]
/**
 * Contract v2: one runtime-emitted step per tool call, upserted by `id` (running -> done | error).
 * Old payloads carried only `stage` and `message`; they have no id and no status.
 */
export interface StepPayload {
  id?: string
  status?: StepStatus
  kind?: StepKind
  label?: string
  detail?: string | null
  stage?: Stage | null
  /** Legacy label. */
  message?: string
}
export interface QuestionOption { label: string; hint?: string; default?: boolean }
export interface QuestionPayload { question: string; details?: string; why: string; options?: QuestionOption[] }
export interface CheckpointPayload { stage: CheckpointStage; summary_markdown: string; options: string[] }
export interface DocumentRequestedPayload { description: string; why: string; needed_for_stage: Stage }
export const TILE_KINDS = ["info", "finding", "warning", "quantum", "next"] as const
export type TileKind = (typeof TILE_KINDS)[number]
/** `takeaway` (one line, <= 90 chars) is absent on old payloads; the UI derives it from the body. */
export interface AnswerTile { title: string; takeaway?: string; body: string; kind: TileKind }
/** The agent's structured reply: a lead line and markdown tiles. */
export interface AnswerPayload { head: string; tiles: AnswerTile[] }
export interface SectionDeltaPayload { section_id: string; delta: string }
/** `markdown` (the section body, no heading) is absent on old payloads. */
export interface SectionSavedPayload { section_id: string; version: number; author: Author; markdown?: string }
/** Token usage for a run; present only when the backend reports it. */
export interface Usage { prompt_tokens: number; output_tokens: number; total_tokens: number }

export interface RunAwaitingUserPayload { kind: PendingKind; usage?: Usage }
export interface RunFinishedPayload { summary: string; usage?: Usage }
export interface RunErrorPayload { code: string; message: string }
export interface DocProgressPayload {
  doc_id: string; status: DocStatus; pages_done: number; pages_total: number
  /** Sent by the document store alongside the contract fields; not in contract.py yet. */
  filename?: string; pages_failed?: number
}
export interface DocReadyPayload { doc_id: string; pages_total: number; summary?: Record<string, unknown> | null }
export interface DocErrorPayload {
  doc_id: string; code: string; message: string
  /** Sent by the document store alongside the contract fields; not in contract.py yet. */
  reason?: string
}

// ---- The run-through page (handoff 4): shared with the OIO simulator ----

export type { DefaultChoice, Row, RowAction, RowKind, StopKind } from "../order-sim/runThrough"
export type StageLandedPayload = RT.StageLandedPayload<Stage>
export type RowChangedPayload = RT.RowChangedPayload<Stage>
export type RunRerunPayload = RT.RunRerunPayload<Stage>
export type StopPayload = RT.StopPayload<Stage>

/** One SSE frame / replayed row. `thread_seq` is per thread and strictly increasing. */
export type OiaEvent =
  | { type: "run.started"; payload: RunStartedPayload }
  | { type: "step"; payload: StepPayload }
  | { type: "question"; payload: QuestionPayload }
  | { type: "checkpoint"; payload: CheckpointPayload }
  | { type: "document.requested"; payload: DocumentRequestedPayload }
  | { type: "answer"; payload: AnswerPayload }
  | { type: "section.delta"; payload: SectionDeltaPayload }
  | { type: "section.saved"; payload: SectionSavedPayload }
  | { type: "run.awaiting_user"; payload: RunAwaitingUserPayload }
  | { type: "run.finished"; payload: RunFinishedPayload }
  | { type: "run.error"; payload: RunErrorPayload }
  | { type: "doc.progress"; payload: DocProgressPayload }
  | { type: "doc.ready"; payload: DocReadyPayload }
  | { type: "doc.error"; payload: DocErrorPayload }
  | { type: "stage.landed"; payload: StageLandedPayload }
  | { type: "row.changed"; payload: RowChangedPayload }
  | { type: "run.rerun"; payload: RunRerunPayload }
  | { type: "stop"; payload: StopPayload }
  /** The legal sources a landed stage cites (contract.py `Sources`); the shape is order-sim/sources.ts. */
  | { type: "sources"; payload: SourcesPayload<Stage> }

export type OiaEventType = OiaEvent["type"]

export const EVENT_TYPES: readonly OiaEventType[] = [
  "run.started", "step", "question", "checkpoint", "document.requested", "answer", "section.delta", "section.saved",
  "run.awaiting_user", "run.finished", "run.error", "doc.progress", "doc.ready", "doc.error",
  "stage.landed", "row.changed", "run.rerun", "stop", "sources",
]

/** Wire envelope around every event. `thread_seq` is the replay cursor; the rest is informational. */
export type EventFrame = OiaEvent & {
  thread_seq: number
  run_id?: string
  created_at?: string
}

/**
 * One document-stream frame (backend docs/models.py DocEvent). The cursor is `seq`, a per-thread document
 * sequence, not thread_seq. `doc_id` sits at the top level; the payload carries the counts.
 */
export interface DocFrame {
  seq: number
  type: "doc.progress" | "doc.ready" | "doc.error"
  doc_id: string
  payload: Record<string, unknown>
}

// ---- REST shapes ----

export interface ThreadRow {
  id: string
  title: string
  mode: SimMode
  status: string
  /** The Nira case file the draft was started from; older servers omit it. */
  case_id?: string | null
  /** The Nira documents this draft was started with (absent or null: every document of the workspace). */
  selected_doc_ids?: string[] | null
  /** The feature the row belongs to, as the server tags it (oio_draft or oia_draft; older servers omit it). */
  feature?: "oio_draft" | "oia_draft" | null
  /** A document was added or a turn run; false for a draft nobody touched. Older servers omit it. */
  active?: boolean
  created_at: string
  updated_at: string
}

/** One case file's draft, for the case file's home: a turn ran on it, and its landed stages. */
export interface CaseDraftRow extends ThreadRow {
  stages: Partial<Record<Stage, StageLandedPayload & { stale?: boolean }>>
}

export interface PendingQuestion { kind: PendingKind; [key: string]: unknown }

/** GET /threads/{id} and POST /threads (api/threads.py thread_view): the thread fields sit at the top level. */
/** Caption facts from the latest intake record (GET /threads/{id}); null when not stated. */
export interface CaseFacts {
  appeal_kind: string | null
  appellant_name: string | null
  respondent_name: string | null
  taxpayer_name: string | null
  gstin: string | null
  impugned_order_no: string | null
  impugned_order_date: string | null
  communication_date: string | null
  filing_date: string | null
}

export interface ThreadSnapshot extends ThreadRow {
  /** Highest thread_seq committed when the snapshot was read. */
  last_seq: number
  pending_question: PendingQuestion | null
  current_draft_version: number
  case_summary: Record<string, unknown>
  stale_stages: Stage[]
  /** Older servers omit it. */
  case_facts?: CaseFacts
  /** Every landed stage as the page shows it (handoff 4); older servers omit it. */
  stages?: Partial<Record<Stage, StageLandedPayload & { stale?: boolean }>>
}

export interface SectionDraft { markdown: string; version: number; author: Author }

/** GET /threads/{id}/draft: sections arrive as a list in canonical order (api/drafts.py). */
export interface DraftWire {
  version: number
  sections: Array<{ section_id: string } & SectionDraft>
  /** Ground points keyed by section id ("findings.G1"); older servers omit it. */
  titles?: Record<string, string>
  /** Section ids the finished draft must contain; null until known. Older servers omit it. */
  expected?: string[] | null
}

/** The draft as the store holds it: section ids map to their latest save. */
export interface DraftSnapshot {
  version: number
  sections: Record<string, SectionDraft>
  titles?: Record<string, string>
  expected?: string[] | null
}

export interface SectionVersion { version: number; markdown: string; author: Author; note: string | null; created_at: string }

export interface DocumentRow {
  doc_id: string
  thread_id: string
  filename: string
  mime: string
  size_bytes: number
  status: DocStatus
  pages_total: number
  pages_done: number
  pages_failed: number
  summary: Record<string, unknown> | null
  error: string | null
  created_at: string
  updated_at: string
}
